import { extensionsConfig } from "client/config/extensionsConfig";
import { useDeviceUpload } from "client/hooks/upload/useDeviceUpload";
import { useGoogleDriveUpload } from "client/hooks/upload/useGoogleDriveUpload";
import { capitalize } from "client/utils/capitalize";
import { translateFunc } from "client/utils/translateFunc";
import { ArrowIcon } from "components/common/Icons/ArrowIcon";
import { UploadIcon } from "components/common/Icons/UploadIcon";
import { List } from "components/common/List/List";
import { Popup } from "components/common/Popup/Popup";
import { DeviceUpload } from "components/common/Upload/DeviceUpload/DeviceUpload";
import { DropboxUpload } from "components/common/Upload/DropboxUpload/DropboxUpload";
import { GoogleDriveAccessErrorModal } from "components/common/Upload/GoogleDriveUpload/GoogleDriveAccessErrorModal/GoogleDriveAccessErrorModal";
import { GoogleDriveUpload } from "components/common/Upload/GoogleDriveUpload/GoogleDriveUpload";
import { UrlUpload } from "components/common/Upload/UrlUpload/UrlUpload";
import { useUploadImage } from "./useUploadImage";

const supportedExtensions = extensionsConfig.rotate;

export const UploadImage = () => {
    const { isOpen, toggle, close, handleFile, onUploadStart, handleClick } = useUploadImage();
    const { handleClick: handleDeviceUploadClick } = useDeviceUpload(
        handleFile,
        { allowMultiple: false, supportedExtensions },
        "flip-rotate-file-input",
    );
    const {
        handleClick: handleGoogleDriveUploadClick,
        accessError,
        setAccessError,
        isLoading: isGoogleDriveLoading,
    } = useGoogleDriveUpload(handleFile, { allowMultiple: false, supportedExtensions }, isOpen);

    return (
        <div className="py-4">
            <GoogleDriveAccessErrorModal
                isOpen={accessError}
                close={() => setAccessError(false)}
                handleRetry={handleGoogleDriveUploadClick}
            />
            <Popup
                isOpen={isOpen}
                close={close}
                content={
                    <div className="absolute bg-white dark:bg-darkSurface-200 left-0 right-0 border dark:border-darkSurface-400 shadow-xl rounded-xl my-2 flex flex-col gap-2 overflow-hidden">
                        <List
                            items={{
                                from_device: (
                                    <DeviceUpload
                                        handleFile={handleFile}
                                        htmlFileInputId="flip-rotate-file-input"
                                        options={{
                                            allowMultiple: false,
                                            onClick: handleClick,
                                            supportedExtensions,
                                        }}
                                    />
                                ),
                                dropbox: (
                                    <DropboxUpload
                                        handleFile={handleFile}
                                        options={{
                                            allowMultiple: false,
                                            uploadStart: onUploadStart,
                                            onClick: handleClick,
                                            supportedExtensions,
                                        }}
                                        isOpen={isOpen}
                                    />
                                ),
                                google_drive: (
                                    <GoogleDriveUpload
                                        options={{
                                            allowMultiple: false,
                                            uploadStart: onUploadStart,
                                            onClick: () => {
                                                handleClick();
                                                handleGoogleDriveUploadClick();
                                            },
                                            supportedExtensions,
                                        }}
                                        isLoading={isGoogleDriveLoading}
                                    />
                                ),
                                url_upload: (
                                    <UrlUpload
                                        handleFile={handleFile}
                                        options={{
                                            uploadStart: onUploadStart,
                                            onClick: handleClick,
                                            supportedExtensions,
                                        }}
                                    />
                                ),
                            }}
                        />
                    </div>
                }
            >
                <div className="cursor-pointer flex rounded-xl overflow-hidden w-full">
                    <div
                        className="flex-grow flex justify-center items-center gap-4 text-white font-semibold py-2 border-r border-r-white dark:border-r-darkSurface-400 bg-irBlue-200 dark:bg-darkSurface-200 hover:bg-irBlue-400 dark:hover:bg-darkSurface-300"
                        onClick={() => {
                            handleDeviceUploadClick();
                            handleClick();
                        }}
                        data-testid={`device-upload`}
                    >
                        <div className="">
                            <UploadIcon className="fill-white" width={20} height={20} />
                        </div>
                        <div className="select-none">{capitalize(translateFunc("common:upload-image"))}</div>
                    </div>
                    <div
                        className="flex-grow-0 flex items-center justify-center px-4 bg-irBlue-200 dark:bg-darkSurface-200 hover:bg-irBlue-400 dark:hover:bg-darkSurface-300"
                        onClick={toggle}
                        data-testid={`upload-list-opener`}
                    >
                        {isOpen ? (
                            <div className="rotate-180">
                                <ArrowIcon className="stroke-white" width={10} height={10} />
                            </div>
                        ) : (
                            <div>
                                <ArrowIcon className="stroke-white" width={10} height={10} />
                            </div>
                        )}
                    </div>
                </div>
            </Popup>
        </div>
    );
};
